import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Sparkles, Mic, MessageSquare, Award, Loader2, ArrowRight, Brain, Target, History } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EXAM_META = {
  'rbi-grade-b': { color: 'from-indigo-600 to-blue-700', tagline: 'Phase 3 Personality Interview' },
  'sbi-po': { color: 'from-blue-700 to-cyan-700', tagline: 'SBI Final Interview Panel' },
  'ibps-po': { color: 'from-emerald-600 to-teal-700', tagline: 'IBPS Common Interview' },
  'ib-acio': { color: 'from-red-700 to-rose-800', tagline: 'Intelligence Bureau Panel' },
};

const AIInterviewLanding = () => {
  const [exams, setExams] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'AI Mock Interview — RBI Grade B, SBI PO, IBPS PO, IB ACIO | Vidya365';
    const token = localStorage.getItem('studentToken');
    fetch(`${API_URL}/api/interview/exams`).then((r) => r.json()).then(setExams).catch(() => {});
    if (token) {
      fetch(`${API_URL}/api/interview/history`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => (r.ok ? r.json() : []))
        .then(setHistory)
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const startInterview = async (examId) => {
    const token = localStorage.getItem('studentToken');
    setStarting(examId);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/api/interview/start`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ exam_id: examId }),
      });
      if (!res.ok) throw new Error('Failed to start');
      const data = await res.json();
      navigate(`/ai-interview/session/${data.session_id}`);
    } catch (e) {
      alert('Could not start interview. Please try again.');
    } finally {
      setStarting(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-br from-indigo-700 via-purple-700 to-red-700 text-white py-14 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="bg-yellow-400 text-gray-900 mb-3 px-3 py-1 font-bold">NEW — Powered by AI</Badge>
            <h1 className="text-3xl md:text-5xl font-bold mb-4" data-testid="ai-interview-h1">
              AI Mock Interview Platform
            </h1>
            <p className="text-lg md:text-xl text-indigo-100 mb-6">
              Realistic personality interviews for <strong>RBI Grade B • SBI PO • IBPS PO • IB ACIO</strong>.
              Get scored on every answer with personalised feedback.
            </p>
            <div className="flex flex-wrap justify-center gap-3 text-sm">
              <span className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full"><Brain className="w-4 h-4" /> GPT-powered panel</span>
              <span className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full"><Mic className="w-4 h-4" /> Voice or Text</span>
              <span className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full"><Award className="w-4 h-4" /> 1-10 Score per Answer</span>
              <span className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full"><Target className="w-4 h-4" /> 10 Questions • 20 min</span>
            </div>
          </div>
        </div>
      </section>

      {/* Exam Selection */}
      <section className="py-10">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-2xl font-bold mb-6 text-center">Choose Your Exam</h2>
          <div className="grid md:grid-cols-2 gap-4" data-testid="exam-grid">
            {exams.map((exam) => {
              const meta = EXAM_META[exam.id] || EXAM_META['rbi-grade-b'];
              return (
                <Card key={exam.id} className="overflow-hidden hover:shadow-xl transition-shadow border-2 hover:border-indigo-400">
                  <div className={`h-2 bg-gradient-to-r ${meta.color}`} />
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <Badge variant="outline" className="mb-2 text-xs">{meta.tagline}</Badge>
                        <h3 className="text-xl font-bold">{exam.name}</h3>
                        <p className="text-xs text-gray-500 mt-1">{exam.duration} • 10 questions</p>
                      </div>
                      <Sparkles className="text-yellow-500" size={24} />
                    </div>
                    <Button
                      data-testid={`start-interview-${exam.id}`}
                      disabled={starting === exam.id}
                      onClick={() => startInterview(exam.id)}
                      className={`w-full bg-gradient-to-r ${meta.color} hover:opacity-90 text-white`}
                    >
                      {starting === exam.id ? (
                        <><Loader2 className="animate-spin mr-2" size={16} /> Starting...</>
                      ) : (
                        <>Start Interview <ArrowRight className="ml-2" size={16} /></>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* History */}
      {!loading && history.length > 0 && (
        <section className="py-10 bg-white" data-testid="history-section">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="text-xl font-bold mb-4 flex items-center"><History className="mr-2" size={20} /> Your Past Interviews</h2>
            <div className="divide-y border rounded-lg">
              {history.slice(0, 10).map((h) => (
                <Link
                  key={h.id}
                  to={`/ai-interview/session/${h.id}`}
                  className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                  data-testid={`history-${h.id}`}
                >
                  <div>
                    <p className="font-medium">{h.exam_name}</p>
                    <p className="text-xs text-gray-500">{(h.started_at || '').slice(0, 10)} • {h.status}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {h.final_report?.overall_score && (
                      <Badge className="bg-indigo-100 text-indigo-700">{h.final_report.overall_score}/100</Badge>
                    )}
                    <ArrowRight size={16} className="text-gray-400" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* How it Works */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-2xl font-bold text-center mb-8">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { icon: Brain, title: '1. AI Panel Asks', desc: 'A realistic AI interview panel asks 10 exam-pattern questions, one at a time.' },
              { icon: MessageSquare, title: '2. You Answer', desc: 'Type or speak your answer. Voice mode uses your browser microphone (no install).' },
              { icon: Award, title: '3. Score + Feedback', desc: 'Each answer scored 1-10 with strengths, improvements and a model answer. Final report with verdict.' },
            ].map((step, i) => (
              <Card key={i} className="text-center">
                <CardContent className="p-5">
                  <step.icon className="mx-auto mb-3 text-indigo-600" size={32} />
                  <h3 className="font-bold mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-600">{step.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AIInterviewLanding;
